import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useTurnstile } from "../use-turnstile";

export default function Register() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const { containerRef, getToken, reset, loadingConfig, required: turnstileRequired } = useTurnstile(
    turnstileSiteKey,
    true,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/auth/config");
        const j = (await r.json()) as { turnstileSiteKey?: string };
        if (!cancelled) setTurnstileSiteKey(j.turnstileSiteKey?.trim() ? j.turnstileSiteKey : "");
      } catch {
        if (!cancelled) setTurnstileSiteKey("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (turnstileRequired && !getToken()) {
      setError("Complete the security check before registering.");
      return;
    }
    setBusy(true);
    const r = await api<{ ok?: boolean; needsVerification?: boolean }>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        ...(turnstileRequired ? { turnstileToken: getToken() } : {}),
      }),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error || "Registration failed");
      reset();
      return;
    }
    if (r.data?.needsVerification) {
      navigate("/login", {
        replace: true,
        state: {
          verifyEmailSent: true,
          registeredEmail: email.trim().toLowerCase(),
        },
      });
    }
  }

  return (
    <div className="layout">
      <main className="layout-main layout-main--narrow">
        <h1>Create account</h1>
        <p className="muted">
          Start your personal log. Password must be at least 8 characters. We will email you a link to
          verify your address before you can sign in.
        </p>
        <form className="card" onSubmit={submit}>
          <div className="field">
            <label htmlFor="reg-email">Email</label>
            <input
              id="reg-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="reg-password">Password</label>
            <div className="field-password-wrap">
              <input
                id="reg-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
              <button
                type="button"
                className="field-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                    />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {turnstileRequired ? (
            <div className="field turnstile-field">
              <span className="turnstile-label">Verification</span>
              <div ref={containerRef} className="turnstile-container" />
            </div>
          ) : null}
          {error ? <p className="error">{error}</p> : null}
          <button type="submit" className="btn btn-primary" style={{ width: "100%" }} disabled={busy || loadingConfig}>
            {busy ? "Creating account…" : "Register"}
          </button>
        </form>
        <p className="muted" style={{ marginTop: "1rem" }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
        <p className="legal-auth-links muted">
          <Link to="/terms">Terms</Link>
          {" · "}
          <Link to="/privacy">Privacy</Link>
        </p>
      </main>
    </div>
  );
}
